import { useState, useEffect } from "react";
import {
  Home, FileText, Briefcase, MessageSquare, Route, History,
  HelpCircle, Users, Phone, Eye, Upload,
  GraduationCap, Lightbulb, AlertCircle, Handshake, Smartphone,
  Save, Loader2, Plus, Trash2, ChevronDown, ChevronRight,
  ChevronUp, Pencil, X,
} from "lucide-react";
import { settingsApi } from "@/integrations/mysql/client";
import { useToast } from "@/hooks/use-toast";

// ── Icon registry ──────────────────────────────────────────────────────────────
const ICON_MAP = {
  GraduationCap, Home, FileText, Briefcase, MessageSquare,
  Route, History, HelpCircle, Users, Phone, Eye, Upload,
  Handshake, Smartphone,
} as const;
type IconName = keyof typeof ICON_MAP;

const ICON_OPTIONS: IconName[] = [
  "GraduationCap","Home","FileText","Briefcase","MessageSquare",
  "Route","History","HelpCircle","Users","Phone","Eye","Upload",
  "Handshake","Smartphone",
];

// ── Visual icon picker ─────────────────────────────────────────────────────────
function IconPickerInline({ value, onChange }: { value: IconName; onChange: (v: IconName) => void }) {
  const [open, setOpen] = useState(false);
  const SelectedIcon = ICON_MAP[value] ?? GraduationCap;
  return (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm text-gray-800 bg-white hover:border-brand-primary/50 transition-colors w-full text-left"
      >
        <SelectedIcon className="w-4 h-4 text-brand-primary shrink-0" />
        <span className="flex-1 truncate">{value}</span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-400 shrink-0" />
      </button>
      {open && (
        <div className="border border-gray-200 rounded-xl p-2 bg-white shadow-sm z-10">
          <div className="grid grid-cols-5 gap-1">
            {ICON_OPTIONS.map((name) => {
              const Icon = ICON_MAP[name];
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => { onChange(name); setOpen(false); }}
                  title={name}
                  className={`flex flex-col items-center gap-0.5 px-1 py-1.5 rounded-lg transition-all ${
                    value === name ? "bg-brand-primary text-white" : "hover:bg-brand-primary/8 text-gray-600 hover:text-brand-primary"
                  }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="text-[8px] font-medium leading-tight truncate w-full text-center">{name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Color options ──────────────────────────────────────────────────────────────
const COLOR_OPTIONS = [
  { value: "bg-brand-primary/10 text-brand-primary", label: "Verde" },
  { value: "bg-emerald-100 text-emerald-700",         label: "Esmeralda" },
  { value: "bg-sky-100 text-sky-700",                 label: "Azul" },
  { value: "bg-violet-100 text-violet-700",           label: "Violeta" },
  { value: "bg-pink-100 text-pink-600",               label: "Rosa" },
  { value: "bg-orange-100 text-orange-700",           label: "Laranja" },
  { value: "bg-teal-100 text-teal-700",               label: "Teal" },
  { value: "bg-amber-100 text-amber-700",             label: "Âmbar" },
  { value: "bg-cyan-100 text-cyan-700",               label: "Ciano" },
  { value: "bg-gray-100 text-gray-600",               label: "Cinza" },
  { value: "bg-indigo-100 text-indigo-700",           label: "Índigo" },
  { value: "bg-lime-100 text-lime-700",               label: "Lima" },
  { value: "bg-fuchsia-100 text-fuchsia-700",         label: "Fúcsia" },
  { value: "bg-rose-100 text-rose-600",               label: "Vermelho" },
];

// ── Data types ─────────────────────────────────────────────────────────────────
interface TutorialStep { title: string; text: string; }
interface TutorialSectionData {
  id: string;
  iconName: IconName;
  title: string;
  color: string;
  description: string;
  steps: TutorialStep[];
  tips: string[];
}

// ── Default data (serialisable) ────────────────────────────────────────────────
const DEFAULT_SECTIONS: TutorialSectionData[] = [
  {
    id: "visao-geral", iconName: "GraduationCap",
    title: "Visão Geral do Painel",
    color: "bg-brand-primary/10 text-brand-primary",
    description: "O painel administrativo permite controlar todo o conteúdo do site sem precisar de conhecimento técnico.",
    steps: [
      { title: "Navegação", text: "Use o menu lateral esquerdo (ícone ☰ no topo) para acessar cada seção. Clique no nome de uma seção para expandir ou recolher o grupo." },
      { title: "Salvar alterações", text: "Cada página tem seu próprio botão 'Salvar'. As alterações só ficam visíveis no site após clicar em salvar." },
      { title: "Visualizar o site", text: "Clique em 'Ver site público' no rodapé do menu para abrir o site em uma nova aba e verificar as mudanças." },
    ],
    tips: ["As alterações são salvas no banco de dados e ficam disponíveis imediatamente após salvar.", "Você pode editar e salvar quantas vezes quiser — não há limite."],
  },
  {
    id: "inicio", iconName: "Home",
    title: "Início (Página Principal)",
    color: "bg-emerald-100 text-emerald-700",
    description: "Controla toda a página inicial do site: banner, diferenciais, seção de destaque e ordem das seções.",
    steps: [
      { title: "Aba Hero", text: "Edite o banner principal: foto de fundo, título, subtítulo, botões, lista de benefícios e estatísticas." },
      { title: "Aba Diferenciais", text: "Gerencie os cartões de abordagem clínica. Adicione, edite, reordene ou exclua cada card." },
      { title: "Aba CTA", text: "Edite a seção de chamada para ação: textos, estatísticas e link do WhatsApp." },
      { title: "Aba Ordem das Seções", text: "Arraste as seções para definir a ordem em que aparecem na página inicial. Clique em Salvar Ordem após reordenar." },
    ],
    tips: ["A foto do Hero deve ter proporção 3:4 para melhor resultado.", "O link do WhatsApp deve estar no formato internacional: 5511999999999."],
  },
  {
    id: "instagram-token", iconName: "Smartphone",
    title: "Token do Instagram (Feed)",
    color: "bg-fuchsia-100 text-fuchsia-700",
    description: "O feed do Instagram exige um token de acesso que expira a cada 60 dias. Siga os passos abaixo para gerar um novo token e colá-lo no painel.",
    steps: [
      { title: "Acesse o Facebook Developers", text: "Abra o site developers.facebook.com e entre com a conta do Facebook vinculada ao Instagram da clínica." },
      { title: "Abra o Graph API Explorer", text: "No menu superior, clique em 'Ferramentas' → 'Graph API Explorer'. Selecione o aplicativo correto no canto superior direito." },
      { title: "Gere o token de usuário", text: "Clique em 'Gerar token de acesso'. Na janela que abrir, marque as permissões: instagram_basic e instagram_content_publish (ou apenas instagram_basic para leitura). Clique em 'Gerar token'." },
      { title: "Estenda o token para 60 dias", text: "Copie o token gerado. Abra uma nova aba e acesse: graph.facebook.com/oauth/access_token?grant_type=fb_exchange_token&client_id=SEU_APP_ID&client_secret=SEU_APP_SECRET&fb_exchange_token=SEU_TOKEN_CURTO — substitua os campos e acesse a URL. O JSON retornado conterá o novo token de longa duração." },
      { title: "Cole o token no painel", text: "No painel admin, vá em Início → aba Instagram (ou seção Instagram). Cole o token no campo 'Token de Acesso' e clique em 'Atualizar token'. O feed será atualizado imediatamente." },
      { title: "Renove antes de expirar", text: "O token dura 60 dias. Anote na agenda um lembrete para renovar antes do prazo — caso expire, o feed para de exibir posts novos." },
    ],
    tips: [
      "Se não tiver acesso ao aplicativo no Facebook Developers, peça ao desenvolvedor o App ID e App Secret para poder gerar e estender o token.",
      "Uma alternativa mais simples é usar o site token.gerador.app ou similar que automatiza a extensão do token sem precisar montar a URL manualmente.",
      "O token deve começar com 'EAA...' — se aparecer mensagem de erro, verifique se as permissões instagram_basic foram marcadas.",
    ],
  },
  {
    id: "blog", iconName: "FileText",
    title: "Blog",
    color: "bg-sky-100 text-sky-700",
    description: "Gerencie posts, categorias e o cabeçalho da página de blog.",
    steps: [
      { title: "Aba Posts", text: "Liste, crie, edite e exclua artigos do blog. Use o editor rico para formatar o conteúdo." },
      { title: "Aba Categorias", text: "Crie e gerencie categorias com nome, cor de fundo e cor de texto." },
      { title: "Aba Cabeçalho", text: "Edite o título, descrição e badges que aparecem no topo da página de blog." },
      { title: "Visibilidade", text: "Use o toggle 'Página do Blog' para esconder ou mostrar toda a página de blog do site público." },
    ],
    tips: [],
  },
  {
    id: "servicos", iconName: "Briefcase",
    title: "Serviços",
    color: "bg-violet-100 text-violet-700",
    description: "Edite as informações de cada tipo de atendimento oferecido pela clínica.",
    steps: [
      { title: "Editar serviço", text: "Clique no ícone de lápis ao lado de cada serviço para editar título, descrição, benefícios e imagem." },
      { title: "Reordenar", text: "Arraste os serviços pelo ícone ⠿ para alterar a ordem de exibição." },
      { title: "Visibilidade", text: "Use o toggle no topo da página para esconder ou exibir toda a seção de serviços." },
    ],
    tips: [],
  },
  {
    id: "depoimentos", iconName: "MessageSquare",
    title: "Depoimentos",
    color: "bg-pink-100 text-pink-600",
    description: "Gerencie os depoimentos exibidos na página inicial.",
    steps: [
      { title: "Adicionar depoimento", text: "Clique em 'Novo Depoimento', preencha nome, texto, avaliação (estrelas), cargo e opcionalmente uma foto." },
      { title: "Foto do autor", text: "Clique na área de foto para abrir o editor. Selecione uma imagem, recorte em formato circular e confirme." },
      { title: "Ativar/Ocultar", text: "Clique no botão 'Visível/Oculto' de cada depoimento para controlá-lo individualmente sem excluí-lo." },
      { title: "Reordenar", text: "Arraste pelo ícone ⠿ para definir a ordem de exibição." },
    ],
    tips: [],
  },
  {
    id: "jornada", iconName: "Route",
    title: "Jornada do Paciente",
    color: "bg-orange-100 text-orange-700",
    description: "Edite as etapas do processo de atendimento exibidas na página inicial.",
    steps: [
      { title: "Editar etapa", text: "Clique no lápis para editar o ícone, título e descrição de cada etapa." },
      { title: "Adicionar etapa", text: "Clique em 'Nova Etapa' para adicionar um novo passo à jornada." },
      { title: "Reordenar", text: "Arraste as etapas para reorganizar o fluxo do atendimento." },
    ],
    tips: [],
  },
  {
    id: "historia", iconName: "History",
    title: "Quem Somos",
    color: "bg-teal-100 text-teal-700",
    description: "Edite a história da clínica, linha do tempo e membros da equipe.",
    steps: [
      { title: "Aba Conteúdo", text: "Edite os textos do hero (título, subtítulo, foto), da seção de história e dos princípios." },
      { title: "Aba Linha do Tempo", text: "Adicione marcos históricos com ano, título e descrição. Reordene e ative/desative cada evento." },
      { title: "Aba Equipe", text: "Adicione membros da equipe com foto (com recorte), nome, cargo e descrição." },
    ],
    tips: [],
  },
  {
    id: "faq", iconName: "HelpCircle",
    title: "Perguntas Frequentes (FAQ)",
    color: "bg-amber-100 text-amber-700",
    description: "Gerencie as perguntas e respostas exibidas na seção de FAQ.",
    steps: [
      { title: "Adicionar pergunta", text: "Clique em 'Nova Pergunta', escreva a pergunta e a resposta completa." },
      { title: "Reordenar", text: "Arraste as perguntas para definir a ordem de exibição." },
      { title: "Ativar/Desativar", text: "Desative perguntas temporariamente sem excluí-las." },
    ],
    tips: [],
  },
  {
    id: "contato", iconName: "Phone",
    title: "Contato",
    color: "bg-cyan-100 text-cyan-700",
    description: "Edite o cabeçalho da página de contato e as informações dos canais de atendimento.",
    steps: [
      { title: "Aba Cabeçalho", text: "Edite o título, descrição e badges exibidos no topo da página de contato." },
      { title: "Aba Canais", text: "Atualize o número de WhatsApp, e-mail e URL do Instagram." },
    ],
    tips: ["O número do WhatsApp deve estar no formato internacional sem espaços: 5511999999999"],
  },
  {
    id: "usuarios", iconName: "Users",
    title: "Usuários & Permissões",
    color: "bg-indigo-100 text-indigo-700",
    description: "Gerencie quem tem acesso ao painel administrativo e visualize o histórico de alterações.",
    steps: [
      { title: "Aba Usuários", text: "Veja todos os usuários cadastrados. Clique no ícone de escudo para alternar entre Admin e Paciente." },
      { title: "Adicionar usuário", text: "Clique em 'Novo usuário', preencha nome, e-mail, senha e escolha a permissão." },
      { title: "Excluir usuário", text: "Clique no ícone de lixeira para remover um usuário. Não é possível excluir sua própria conta." },
      { title: "Aba Histórico", text: "Veja todas as alterações feitas no painel: quem editou, o que foi alterado e quando." },
    ],
    tips: ["Nunca compartilhe sua senha. Crie um usuário Admin separado para cada pessoa que precisar de acesso."],
  },
  {
    id: "carrossel", iconName: "Handshake",
    title: "Carrossel de Parceiros",
    color: "bg-lime-100 text-lime-700",
    description: "Gerencie o carrossel de logos e parceiros exibido na página inicial.",
    steps: [
      { title: "Adicionar parceiro", text: "Clique em 'Novo Item', faça o upload do logo, preencha o nome e opcionalmente o link e descrição." },
      { title: "Ativar/Ocultar", text: "Use o toggle de visibilidade de cada item para exibir ou esconder sem excluir." },
      { title: "Reordenar", text: "Arraste pelo ícone ⠿ para definir a ordem de exibição no carrossel." },
    ],
    tips: ["Use logos em fundo transparente (PNG) para melhor resultado visual."],
  },
  {
    id: "mobile", iconName: "Smartphone",
    title: "Página Mobile (Link Tree)",
    color: "bg-fuchsia-100 text-fuchsia-700",
    description: "Gerencie a tela especial exibida para visitantes em celular na primeira visita.",
    steps: [
      { title: "Foto de perfil", text: "Faça o upload de uma foto circular que aparecerá sobre o banner verde." },
      { title: "Nome e título", text: "Edite o nome e o CRN/cargo exibidos abaixo da foto." },
      { title: "Bio", text: "Escreva uma frase curta de apresentação (max. ~120 caracteres)." },
      { title: "Links", text: "Adicione, edite, reordene e ative/desative os botões de link." },
    ],
    tips: ["WhatsApp e Instagram têm estilos visuais distintos — escolha o tipo correto."],
  },
  {
    id: "visibilidade", iconName: "Eye",
    title: "Controle de Visibilidade de Páginas",
    color: "bg-rose-100 text-rose-600",
    description: "Cada seção do painel permite esconder ou mostrar a página correspondente no site público.",
    steps: [
      { title: "Toggle de visibilidade", text: "No topo de cada página de edição há um interruptor verde/cinza. Verde = visível no site; cinza = oculto." },
      { title: "Efeito imediato", text: "A mudança de visibilidade é aplicada imediatamente." },
    ],
    tips: [],
  },
  {
    id: "fotos", iconName: "Upload",
    title: "Upload e Edição de Fotos",
    color: "bg-fuchsia-100 text-fuchsia-700",
    description: "O painel inclui um editor de fotos integrado com recorte para garantir o melhor visual.",
    steps: [
      { title: "Selecionar foto", text: "Clique na área de upload e escolha uma imagem do seu computador. São aceitos JPG, PNG e WebP." },
      { title: "Recortar", text: "Após selecionar, um editor será aberto. Mova e redimensione a área de recorte." },
      { title: "Confirmar", text: "Clique em 'Confirmar' para enviar a foto recortada ao servidor." },
    ],
    tips: ["Tamanho máximo recomendado: 5 MB."],
  },
];

function parseSections(raw: string | undefined): TutorialSectionData[] {
  if (!raw) return DEFAULT_SECTIONS;
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) && p.length > 0 ? p : DEFAULT_SECTIONS;
  } catch { return DEFAULT_SECTIONS; }
}

// ── View mode: accordion item ──────────────────────────────────────────────────
function TutorialItem({ section }: { section: TutorialSectionData }) {
  const [open, setOpen] = useState(false);
  const Icon = ICON_MAP[section.iconName] ?? GraduationCap;

  return (
    <div className={`border rounded-xl overflow-hidden transition-shadow ${open ? "shadow-md border-brand-primary/20" : "border-gray-100 shadow-sm"}`}>
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50/80 transition-colors"
      >
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${section.color}`}>
          <Icon style={{ width: 18, height: 18 }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-800">{section.title}</p>
          <p className="text-xs text-gray-400 mt-0.5 truncate">{section.description}</p>
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-brand-primary shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
      </button>

      {open && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4 bg-white">
          <ol className="space-y-3">
            {section.steps.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="w-5 h-5 rounded-full bg-brand-primary/10 text-brand-primary text-[11px] font-bold flex items-center justify-center shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-gray-700">{step.title}</p>
                  <p className="text-sm text-gray-500 leading-relaxed mt-0.5">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
          {section.tips && section.tips.length > 0 && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 space-y-1.5">
              <p className="flex items-center gap-1.5 text-xs font-bold text-amber-700 uppercase tracking-wide">
                <Lightbulb className="w-3.5 h-3.5" /> Dicas
              </p>
              {section.tips.map((tip, i) => (
                <p key={i} className="text-xs text-amber-700 leading-relaxed">• {tip}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Edit mode: section editor ──────────────────────────────────────────────────
function SectionEditor({
  section, index, total,
  onChange, onDelete, onMoveUp, onMoveDown,
}: {
  section: TutorialSectionData;
  index: number; total: number;
  onChange: (s: TutorialSectionData) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = ICON_MAP[section.iconName] ?? GraduationCap;

  const setStepField = (si: number, field: keyof TutorialStep, val: string) => {
    const steps = section.steps.map((s, i) => i === si ? { ...s, [field]: val } : s);
    onChange({ ...section, steps });
  };
  const addStep = () => onChange({ ...section, steps: [...section.steps, { title: '', text: '' }] });
  const removeStep = (si: number) => onChange({ ...section, steps: section.steps.filter((_, i) => i !== si) });
  const moveStep = (si: number, dir: -1 | 1) => {
    const steps = [...section.steps];
    const ni = si + dir;
    if (ni < 0 || ni >= steps.length) return;
    [steps[si], steps[ni]] = [steps[ni], steps[si]];
    onChange({ ...section, steps });
  };

  const setTip = (ti: number, val: string) => {
    const tips = section.tips.map((t, i) => i === ti ? val : t);
    onChange({ ...section, tips });
  };
  const addTip = () => onChange({ ...section, tips: [...(section.tips ?? []), ''] });
  const removeTip = (ti: number) => onChange({ ...section, tips: (section.tips ?? []).filter((_, i) => i !== ti) });

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* Section header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${section.color}`}>
          <Icon style={{ width: 16, height: 16 }} />
        </div>
        <span className="flex-1 text-sm font-semibold text-gray-700 truncate">{section.title || 'Nova seção'}</span>
        <div className="flex items-center gap-1">
          <button onClick={onMoveUp}   disabled={index === 0}         className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors"><ChevronUp   className="w-3.5 h-3.5 text-gray-500" /></button>
          <button onClick={onMoveDown} disabled={index === total - 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors"><ChevronDown className="w-3.5 h-3.5 text-gray-500" /></button>
          <button onClick={() => setOpen(v => !v)} className="p-1 rounded hover:bg-gray-200 transition-colors">
            <Pencil className="w-3.5 h-3.5 text-brand-primary" />
          </button>
          <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 transition-colors"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
        </div>
      </div>

      {open && (
        <div className="p-4 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Título</label>
              <input
                type="text" value={section.title}
                onChange={e => onChange({ ...section, title: e.target.value })}
                placeholder="Título da seção"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary"
              />
            </div>
            <div className="col-span-2 space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Descrição</label>
              <textarea
                value={section.description}
                onChange={e => onChange({ ...section, description: e.target.value })}
                placeholder="Descrição curta da seção"
                rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary resize-none"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ícone</label>
              <IconPickerInline value={section.iconName} onChange={v => onChange({ ...section, iconName: v })} />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Cor</label>
              <select
                value={section.color}
                onChange={e => onChange({ ...section, color: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/30 bg-white"
              >
                {COLOR_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Passos ({section.steps.length})</label>
              <button onClick={addStep} className="flex items-center gap-1 text-xs text-brand-primary font-semibold hover:underline">
                <Plus className="w-3 h-3" /> Adicionar passo
              </button>
            </div>
            {section.steps.map((step, si) => (
              <div key={si} className="border border-gray-100 rounded-xl p-3 space-y-2 bg-gray-50">
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-brand-primary/10 text-brand-primary text-[10px] font-bold flex items-center justify-center shrink-0">{si + 1}</span>
                  <div className="flex-1 min-w-0">
                    <input
                      type="text" value={step.title}
                      onChange={e => setStepField(si, 'title', e.target.value)}
                      placeholder="Título do passo"
                      className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-brand-primary/30"
                    />
                  </div>
                  <div className="flex gap-0.5 shrink-0">
                    <button onClick={() => moveStep(si, -1)} disabled={si === 0}                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"><ChevronUp   className="w-3 h-3 text-gray-500" /></button>
                    <button onClick={() => moveStep(si,  1)} disabled={si === section.steps.length - 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30"><ChevronDown className="w-3 h-3 text-gray-500" /></button>
                    <button onClick={() => removeStep(si)}                                              className="p-1 rounded hover:bg-red-50"><X className="w-3 h-3 text-red-400" /></button>
                  </div>
                </div>
                <textarea
                  value={step.text}
                  onChange={e => setStepField(si, 'text', e.target.value)}
                  placeholder="Descrição detalhada do passo…"
                  rows={2}
                  className="w-full bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/30 resize-none"
                />
              </div>
            ))}
            {section.steps.length === 0 && (
              <p className="text-xs text-gray-400 italic text-center py-2">Nenhum passo. Clique em "Adicionar passo".</p>
            )}
          </div>

          {/* Tips */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                <Lightbulb className="w-3.5 h-3.5 text-amber-500" /> Dicas ({(section.tips ?? []).length})
              </label>
              <button onClick={addTip} className="flex items-center gap-1 text-xs text-amber-600 font-semibold hover:underline">
                <Plus className="w-3 h-3" /> Adicionar dica
              </button>
            </div>
            {(section.tips ?? []).map((tip, ti) => (
              <div key={ti} className="flex gap-2 items-start">
                <span className="text-amber-500 text-xs mt-2 shrink-0">•</span>
                <input
                  type="text" value={tip}
                  onChange={e => setTip(ti, e.target.value)}
                  placeholder="Texto da dica…"
                  className="flex-1 border border-amber-200 bg-amber-50 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-amber-300"
                />
                <button onClick={() => removeTip(ti)} className="mt-1 p-1 rounded hover:bg-red-50 shrink-0"><X className="w-3 h-3 text-red-400" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
export function TutorialManager() {
  const [sections, setSections] = useState<TutorialSectionData[]>(DEFAULT_SECTIONS);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    settingsApi.list()
      .then(list => {
        const raw = list.find(s => s.key === 'tutorial_config')?.value;
        setSections(parseSections(raw));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (updated: TutorialSectionData) => {
    setSections(prev => prev.map(s => s.id === updated.id ? updated : s));
    setDirty(true);
  };

  const handleDelete = (id: string) => {
    setSections(prev => prev.filter(s => s.id !== id));
    setDirty(true);
  };

  const handleMove = (index: number, dir: -1 | 1) => {
    setSections(prev => {
      const arr = [...prev];
      const ni = index + dir;
      if (ni < 0 || ni >= arr.length) return arr;
      [arr[index], arr[ni]] = [arr[ni], arr[index]];
      return arr;
    });
    setDirty(true);
  };

  const handleAdd = () => {
    const id = `secao_${Date.now()}`;
    setSections(prev => [...prev, {
      id, iconName: 'GraduationCap',
      title: 'Nova seção',
      color: 'bg-brand-primary/10 text-brand-primary',
      description: '',
      steps: [],
      tips: [],
    }]);
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsApi.update('tutorial_config', JSON.stringify(sections));
      toast({ title: 'Tutorial salvo!' });
      setDirty(false);
    } catch {
      toast({ title: 'Erro ao salvar tutorial', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => { setSections(DEFAULT_SECTIONS); setDirty(true); };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-primary" /></div>;
  }

  return (
    <div className="space-y-5">
      {/* Header card */}
      <div className="bg-brand-primary rounded-2xl p-6 text-white relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/5" />
        <div className="absolute -bottom-8 right-10 w-48 h-48 rounded-full bg-white/5" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold">Tutorial do Painel</h2>
                <p className="text-white/60 text-xs">Aprenda a usar todas as funcionalidades</p>
              </div>
            </div>
            <p className="text-white/75 text-sm leading-relaxed">
              {editMode
                ? 'Modo de edição ativo. Adicione, remova e edite seções, passos e dicas do tutorial.'
                : 'Este tutorial explica como gerenciar cada seção do site. Clique em qualquer item para ver o passo a passo.'}
            </p>
          </div>
          <button
            onClick={() => setEditMode(v => !v)}
            className={`shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-2 rounded-xl transition-all ${
              editMode
                ? 'bg-white text-brand-primary'
                : 'bg-white/20 hover:bg-white/30 text-white'
            }`}
          >
            {editMode ? <><X className="w-3.5 h-3.5" /> Fechar edição</> : <><Pencil className="w-3.5 h-3.5" /> Editar tutorial</>}
          </button>
        </div>
      </div>

      {/* Alert */}
      {!editMode && (
        <div className="flex items-start gap-3 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3">
          <AlertCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-700 leading-relaxed">
            <strong>Lembre-se:</strong> Sempre clique em <strong>Salvar</strong> após fazer alterações. As mudanças não são aplicadas automaticamente.
          </p>
        </div>
      )}

      {/* VIEW MODE */}
      {!editMode && (
        <div className="space-y-2">
          {sections.map(section => (
            <TutorialItem key={section.id} section={section} />
          ))}
        </div>
      )}

      {/* EDIT MODE */}
      {editMode && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">{sections.length} seção(ões) · Clique no lápis de cada seção para expandir e editar</p>
            <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600 underline transition-colors">
              Restaurar padrão
            </button>
          </div>

          {sections.map((section, index) => (
            <SectionEditor
              key={section.id}
              section={section}
              index={index}
              total={sections.length}
              onChange={handleChange}
              onDelete={() => handleDelete(section.id)}
              onMoveUp={() => handleMove(index, -1)}
              onMoveDown={() => handleMove(index, 1)}
            />
          ))}

          <button
            onClick={handleAdd}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-200 hover:border-brand-primary/40 hover:bg-brand-primary/5 text-gray-400 hover:text-brand-primary text-sm font-medium py-3 rounded-xl transition-all"
          >
            <Plus className="w-4 h-4" /> Adicionar seção
          </button>

          {dirty && (
            <div className="flex items-center justify-between bg-brand-primary/5 border border-brand-primary/20 rounded-xl px-4 py-3">
              <p className="text-xs text-brand-primary font-medium">Alterações não salvas</p>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary/90 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar tutorial
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
